import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { RootState } from '../../stores/store';
import backgroundImage from '../../assets/images/pvx-home-bg.png';
import pvxLogo from '../../assets/icons/pvx-logo-full.svg';
import TranslateIcon from '@mui/icons-material/Translate';
import LanguageSelector from '../../components/SideNav/components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { useUserLogoutMutation } from '@api/userDetails';
import UserAuth from '@services/auth';
import useAuth from '@hooks/useAuth';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LogoutIcon from '@mui/icons-material/Logout';
import pvxCapitalImage from '../../assets/images/pvx-capital.png';
import pvxLambdaImage from '../../assets/images/pvx-lambda.png';
import pvxDemoImage from '../../assets/images/pvx-demo.png';
import pendingAccessHeaderImg from '../../assets/images/pending-access-header-img.png';

const PendingAccess: FC = () => {
  const { t: translations } = useTranslation();
  const accessRequest = useSelector((state: RootState) => state?.UserAccessData?.accessRequest);
  const [languageSelectorAnchorEl, setLanguageSelectorAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const [userLogout] = useUserLogoutMutation();
  const { loginUrl } = useAuth();

  const logoutUser = async () => {
    await userLogout({});
    UserAuth.clearTokens();

    setTimeout(() => {
      window.location.href = loginUrl;
    }, 1000);
  };

  const companyName = (accessRequest?.data as any)?.companyName || '';
  const ownerEmail = (accessRequest?.data as any)?.ownerEmail || '';
  const expiresInDays = (accessRequest?.data as any)?.expiresInDays;

  const expiryLabel = useMemo(() => {
    if (typeof expiresInDays !== 'number') {
      return '';
    }
    const d = new Date();
    d.setDate(d.getDate() + expiresInDays);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }, [expiresInDays]);

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: { xs: '0 0 auto', md: '0 0 28%' },
          minWidth: { md: 200 },
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'top',
          color: '#FFFFFF',
          padding: { xs: '1.25rem', md: '1.5rem 1.5rem 1.5rem 2rem' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <img src={pvxLogo} alt="PvX Partners" style={{ height: 33 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <TranslateIcon sx={{ color: '#FFFFFF' }} />
            <Box
              sx={{
                '& .MuiButton-root': {
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                },
                '& .MuiButton-endIcon svg': {
                  color: '#FFFFFF',
                },
              }}
            >
              <LanguageSelector
                anchorEl={languageSelectorAnchorEl}
                setAnchorEl={setLanguageSelectorAnchorEl}
              />
            </Box>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize: '1rem', opacity: 0.9, mb: '1.5rem' }}>
            {translations('access_pending.explore_pvx_partners')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
            <Box sx={{ display: 'flex', gap: '0.75rem' }}>
              <Box
                component="img"
                src={pvxCapitalImage}
                alt="PvX Capital"
                sx={{ width: 32, height: 32, mt: '0.125rem' }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, mb: '0.25rem', fontSize: '1.05rem' }}>
                  {translations('access_pending.pvx_capital')}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', opacity: 0.85, mb: '0.5rem' }}>
                  {translations('access_pending.pvx_capital_description')}
                </Typography>
                <Button
                  variant="text"
                  endIcon={<OpenInNewIcon />}
                  onClick={() =>
                    window.open('https://pvxpartners.com/funding', '_blank', 'noopener,noreferrer')
                  }
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    color: '#51B8B0',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    '&:hover': { background: 'transparent' },
                  }}
                >
                  {translations('access_pending.visit_website')}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '0.75rem' }}>
              <Box
                component="img"
                src={pvxLambdaImage}
                alt="PvX Lambda"
                sx={{ width: 32, height: 32, mt: '0.125rem' }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, mb: '0.25rem', fontSize: '1.05rem' }}>
                  {translations('access_pending.pvx_lambda')}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', opacity: 0.85, mb: '0.5rem' }}>
                  {translations('access_pending.pvx_lambda_description')}
                </Typography>
                <Button
                  variant="text"
                  endIcon={<OpenInNewIcon />}
                  onClick={() =>
                    window.open('https://pvxpartners.com/lambda', '_blank', 'noopener,noreferrer')
                  }
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    color: '#51B8B0',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    '&:hover': { background: 'transparent' },
                  }}
                >
                  {translations('access_pending.visit_website')}
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: '0.75rem' }}>
              <Box
                component="img"
                src={pvxDemoImage}
                alt="Interactive Demo"
                sx={{ width: 32, height: 32, mt: '0.125rem' }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, mb: '0.25rem', fontSize: '1.05rem' }}>
                  {translations('access_pending.interactive_demo')}
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', opacity: 0.85, mb: '0.5rem' }}>
                  {translations('access_pending.interactive_demo_description')}
                </Typography>
                <Button
                  variant="text"
                  endIcon={<OpenInNewIcon />}
                  onClick={() =>
                    window.open('https://pvxpartners.com/', '_blank', 'noopener,noreferrer')
                  }
                  sx={{
                    padding: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    color: '#51B8B0',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    '&:hover': { background: 'transparent' },
                  }}
                >
                  {translations('access_pending.visit_website')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: { xs: '1.25rem', md: '-0.5rem' } }}>
          <Typography sx={{ fontSize: '0.95rem', opacity: 0.9 }}>
            {translations('access_pending.need_help')}{' '}
            <Box
              component="a"
              href="mailto:info@pvxpartners.com"
              sx={{
                color: '#51B8B0',
                fontWeight: 700,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {translations('access_pending.contact_support')}
            </Box>
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          background: '#FFFFFF',
          padding: { xs: '1.25rem', md: '1.5rem 2rem' },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="text"
            onClick={logoutUser}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#51B8B0',
              '&:hover': { background: 'transparent' },
            }}
            startIcon={<LogoutIcon />}
          >
            {translations('common.sign_out')}
          </Button>
        </Box>

        <Box
          sx={{
            maxWidth: 560,
            margin: '0 auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={pendingAccessHeaderImg}
            alt="Pending Access"
            sx={{ width: 72, height: 72, mb: '1rem' }}
          />
          <Typography
            sx={{
              fontSize: { xs: '1.65rem', sm: '2rem' },
              fontWeight: 700,
              color: '#162936',
              mb: '0.75rem',
            }}
          >
            {translations('access_pending.access_pending')}
          </Typography>

          <Typography sx={{ color: '#6C6C6C', fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
            {translations('access_pending.access_pending_message_1', { companyName })}
          </Typography>
          <Typography
            sx={{ color: '#6C6C6C', fontSize: { xs: '0.95rem', sm: '1.05rem' }, mb: '2rem' }}
          >
            {translations('access_pending.access_pending_message_2')}
          </Typography>

          <Box
            sx={{
              maxWidth: '25rem',
              backgroundColor: '#F0F0F160',
              border: '1px solid #E8EAEB',
              borderRadius: '0.75rem',
              padding: '1rem',
              display: 'grid',
              gridTemplateColumns: '160px 1fr',
              rowGap: '1rem',
              columnGap: '0.5rem',
            }}
          >
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#162936' }}>
              {translations('access_pending.comapny_owner')}
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', color: '#162936' }}>{ownerEmail}</Typography>

            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#162936' }}>
              {translations('access_pending.request_status')}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0.25rem 0.75rem',
                borderRadius: '1rem',
                background: '#FFE6CC80',
                color: '#FF8A00',
                fontWeight: 700,
                fontSize: '0.95rem',
                width: 'fit-content',
              }}
            >
              {translations('access_pending.request_status_pending')}
            </Box>

            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#162936' }}>
              {translations('access_pending.request_expiry')}
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', color: '#162936' }}>{expiryLabel}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PendingAccess;
